import React, { Fragment } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import PanoramaFishEye from '@material-ui/icons/PanoramaFishEye';
import CheckIcon from '@material-ui/icons/Check';
import { PhaseTypeEnum, PhaseStructureTypeEnum } from '@vatos-pas/common';
import {
  isSkippedPhase,
  isStartingApprovedPhase,
  getPhaseByType,
} from 'utils/phaseUtils';

import { useStyles } from './StatusBarStyles';
import { isPhaseIncluded } from 'utils/optionalWorkflowPhases';
import {
  getPhaseDisplayName,
  getPhasesForJobType,
} from '../constants/phaseConfigurations';
import { Job } from '../containers/useMasterSheetContainer';
import { useResponsive } from '../hooks/useResponsive';
import { dateWithoutServerTimezone } from '../utils/dateWithoutServerTimezone';

interface StatusBarProps {
  theJob: any;
  getPhase: any;
  isInWarranty: any;
  showBump: any;
}

const getCircleColor = (
  theJob: Job,
  phaseType: PhaseTypeEnum,
  classes: any,
  getPhase: any,
) => {
  try {
    const phaseJob = getPhase(theJob.jobPhases, phaseType);
    if (!phaseJob || !phaseJob.dateStart) return classes.circleGray;
    if (phaseJob.approved) return classes.circleGreen;
    const today = new Date();
    if (
      phaseJob.dateDue &&
      new Date(phaseJob.dateDue) < today &&
      !phaseJob.approved
    ) {
      return classes.circleRed;
    }
    return classes.circle;
  } catch (error) {
    return classes.circleGray;
  }
};

const isPhaseCompleted = (newJob: Job, phaseType: PhaseTypeEnum): boolean => {
  const jobPhase = newJob.jobPhases.find(
    (jobPhase: any) => jobPhase.phaseType === phaseType,
  );
  if (jobPhase) {
    return jobPhase.approved;
  }

  return false;
};

const TimelineJobPhase = (props: {
  job: Job;
  phaseType: PhaseTypeEnum;
  className: string;
  getPhase: any;
}) => {
  const { job, getPhase } = props;
  try {
    const jobPhase = getPhase(job.jobPhases, props.phaseType);
    if (!jobPhase.dateStart) {
      return <Fragment />;
    }
    const dateStart = new Date(jobPhase.dateStart);
    return (
      <Typography className={props.className}>
        {dateStart.getMonth() + 1}/{dateStart.getDate()}/
        {dateStart.getFullYear()}
      </Typography>
    );
  } catch (error) {
    return <Fragment />;
  }
};

export const StatusBar: React.FC<StatusBarProps> = ({
  theJob,
  showBump,
  getPhase,
  isInWarranty,
}) => {
  const PhaseName = theJob.jobPhase.phaseName;
  const { isMobile } = useResponsive();

  const JobPriority = theJob.jobPhase.priority;
  const showBump4 = showBump(PhaseTypeEnum.Bump4);
  const showBump5 = showBump(PhaseTypeEnum.Bump5);
  const isBump1PhaseCompleted = isPhaseCompleted(theJob, PhaseTypeEnum.Bump1);
  const isBump2PhaseCompleted = isPhaseCompleted(theJob, PhaseTypeEnum.Bump2);
  const isBump3PhaseCompleted = isPhaseCompleted(theJob, PhaseTypeEnum.Bump3);
  const isBump4PhaseCompleted = isPhaseCompleted(theJob, PhaseTypeEnum.Bump4);
  const isBump5PhaseCompleted = isPhaseCompleted(theJob, PhaseTypeEnum.Bump5);

  const bump1Phase = getPhaseByType(theJob.jobPhases, PhaseTypeEnum.Bump1);
  const bump2Phase = getPhaseByType(theJob.jobPhases, PhaseTypeEnum.Bump2);
  const bump3Phase = getPhaseByType(theJob.jobPhases, PhaseTypeEnum.Bump3);
  const bump4Phase = getPhaseByType(theJob.jobPhases, PhaseTypeEnum.Bump4);
  const bump5Phase = getPhaseByType(theJob.jobPhases, PhaseTypeEnum.Bump5);
  const isBump1Starting =
    bump1Phase && isStartingApprovedPhase(bump1Phase, theJob);
  const isBump2Starting =
    bump2Phase && isStartingApprovedPhase(bump2Phase, theJob);
  const isBump3Starting =
    bump3Phase && isStartingApprovedPhase(bump3Phase, theJob);
  const isBump4Starting =
    bump4Phase && isStartingApprovedPhase(bump4Phase, theJob);
  const isBump5Starting =
    bump5Phase && isStartingApprovedPhase(bump5Phase, theJob);

  const displayBump1Text = isMobile
    ? !isBump1PhaseCompleted && !isBump1Starting
    : true;
  const displayBump2Text = isMobile
    ? isBump1PhaseCompleted && !isBump2PhaseCompleted && !isBump2Starting
    : true;
  const displayBump3Text = isMobile
    ? isBump2PhaseCompleted && !isBump3PhaseCompleted && !isBump3Starting
    : true;
  const displayBump4Text = isMobile
    ? isBump3PhaseCompleted &&
      !isBump4PhaseCompleted &&
      showBump4 &&
      !isBump4Starting
    : true;
  const displayBump5Text = isMobile
    ? isBump4PhaseCompleted && !isBump5PhaseCompleted && !isBump5Starting
    : true;

  const BumpTimeLine = () => {
    const classes = useStyles();

    return (
      <Box className={isMobile ? classes.whiteBoxMobile : classes.whiteBox}>
        <Box className={classes.timeline}>
          <Fragment>
            <Box className={classes.statusItem}>
              <Box className={classes.circleGreen}>
                <CheckIcon className={classes.iconCircle} />
              </Box>
              {!isMobile && (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Typography className={classes.statusText}>
                    Delivery Complete
                  </Typography>
                  <TimelineJobPhase
                    job={theJob}
                    getPhase={getPhase}
                    className={classes.dateText}
                    phaseType={PhaseTypeEnum.Spraying}
                  />
                </Box>
              )}
            </Box>
            <Box
              className={
                isMobile ? classes.transparentSeparator : classes.separator
              }
            />
            <Box className={classes.statusItem}>
              {isBump1Starting ? (
                <Box className={classes.circleGray}>
                  <CheckIcon className={classes.iconCircle} />
                </Box>
              ) : isBump1PhaseCompleted ? (
                <Box className={classes.circleGreen}>
                  <CheckIcon className={classes.iconCircle} />
                </Box>
              ) : (
                <Box
                  className={getCircleColor(
                    theJob,
                    PhaseTypeEnum.Bump1,
                    classes,
                    getPhase,
                  )}
                >
                  <Typography className={classes.statusNumber}>1</Typography>
                </Box>
              )}
              {displayBump1Text && (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Typography
                    className={
                      isBump1Starting
                        ? `${classes.statusText} ${classes.skippedPhaseText}`
                        : classes.statusText
                    }
                  >
                    {isBump1Starting ? 'Bump Out 1 Skipped' : 'Bump Out 1'}
                  </Typography>
                  {!isBump1Starting && (
                    <TimelineJobPhase
                      job={theJob}
                      getPhase={getPhase}
                      className={classes.dateText}
                      phaseType={PhaseTypeEnum.Bump1}
                    />
                  )}
                </Box>
              )}
            </Box>
            <Box
              className={
                isMobile ? classes.transparentSeparator : classes.separator
              }
            />
            <Box className={classes.statusItem}>
              {isBump2Starting ? (
                <Box className={classes.circleGray}>
                  <CheckIcon className={classes.iconCircle} />
                </Box>
              ) : isBump2PhaseCompleted ? (
                <Box className={classes.circleGreen}>
                  <CheckIcon className={classes.iconCircle} />
                </Box>
              ) : (
                <Box
                  className={getCircleColor(
                    theJob,
                    PhaseTypeEnum.Bump2,
                    classes,
                    getPhase,
                  )}
                >
                  <Typography className={classes.statusNumber}>2</Typography>
                </Box>
              )}
              {displayBump2Text && (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Typography
                    className={
                      isBump2Starting
                        ? `${classes.statusText} ${classes.skippedPhaseText}`
                        : classes.statusText
                    }
                  >
                    {isBump2Starting ? 'Bump Out 2 Skipped' : 'Bump Out 2'}
                  </Typography>
                  {!isBump2Starting && (
                    <TimelineJobPhase
                      job={theJob}
                      getPhase={getPhase}
                      className={classes.dateText}
                      phaseType={PhaseTypeEnum.Bump2}
                    />
                  )}
                </Box>
              )}
            </Box>
            <Box
              className={
                isMobile ? classes.transparentSeparator : classes.separator
              }
            />
            <Box className={classes.statusItem}>
              {isBump3Starting ? (
                <Box className={classes.circleGray}>
                  <CheckIcon className={classes.iconCircle} />
                </Box>
              ) : isBump3PhaseCompleted ? (
                <Box className={classes.circleGreen}>
                  <CheckIcon className={classes.iconCircle} />
                </Box>
              ) : (
                <Box
                  className={getCircleColor(
                    theJob,
                    PhaseTypeEnum.Bump3,
                    classes,
                    getPhase,
                  )}
                >
                  <Typography className={classes.statusNumber}>3</Typography>
                </Box>
              )}
              {displayBump3Text && (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Typography
                    className={
                      isBump3Starting
                        ? `${classes.statusText} ${classes.skippedPhaseText}`
                        : classes.statusText
                    }
                  >
                    {isBump3Starting ? 'Bump Out 3 Skipped' : 'Bump Out 3'}
                  </Typography>
                  {!isBump3Starting && (
                    <TimelineJobPhase
                      job={theJob}
                      getPhase={getPhase}
                      className={classes.dateText}
                      phaseType={PhaseTypeEnum.Bump3}
                    />
                  )}
                </Box>
              )}
            </Box>
            {showBump4 && (
              <Fragment>
                <Box
                  className={
                    isMobile ? classes.transparentSeparator : classes.separator
                  }
                />
                <Box className={classes.statusItem}>
                  {isBump4Starting ? (
                    <Box className={classes.circleGray}>
                      <CheckIcon className={classes.iconCircle} />
                    </Box>
                  ) : isBump4PhaseCompleted ? (
                    <Box className={classes.circleGreen}>
                      <CheckIcon className={classes.iconCircle} />
                    </Box>
                  ) : (
                    <Box
                      className={getCircleColor(
                        theJob,
                        PhaseTypeEnum.Bump4,
                        classes,
                        getPhase,
                      )}
                    >
                      <Typography className={classes.statusNumber}>
                        4
                      </Typography>
                    </Box>
                  )}
                  {displayBump4Text && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Typography
                        className={
                          isBump4Starting
                            ? `${classes.statusText} ${classes.skippedPhaseText}`
                            : classes.statusText
                        }
                      >
                        {isBump4Starting ? 'Bump Out 4 Skipped' : 'Bump Out 4'}
                      </Typography>
                      {!isBump4Starting && (
                        <TimelineJobPhase
                          job={theJob}
                          getPhase={getPhase}
                          className={classes.dateText}
                          phaseType={PhaseTypeEnum.Bump4}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </Fragment>
            )}
            {showBump5 && (
              <Fragment>
                <Box
                  className={
                    isMobile ? classes.transparentSeparator : classes.separator
                  }
                />
                <Box className={classes.statusItem}>
                  {isBump5Starting ? (
                    <Box className={classes.circleGray}>
                      <CheckIcon className={classes.iconCircle} />
                    </Box>
                  ) : isBump5PhaseCompleted ? (
                    <Box className={classes.circleGreen}>
                      <CheckIcon className={classes.iconCircle} />
                    </Box>
                  ) : (
                    <Box
                      className={getCircleColor(
                        theJob,
                        PhaseTypeEnum.Bump5,
                        classes,
                        getPhase,
                      )}
                    >
                      <Typography className={classes.statusNumber}>
                        5
                      </Typography>
                    </Box>
                  )}
                  {displayBump5Text && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Typography
                        className={
                          isBump5Starting
                            ? `${classes.statusText} ${classes.skippedPhaseText}`
                            : classes.statusText
                        }
                      >
                        {isBump5Starting ? 'Bump Out 5 Skipped' : 'Bump Out 5'}
                      </Typography>
                      {!isBump5Starting && (
                        <TimelineJobPhase
                          job={theJob}
                          getPhase={getPhase}
                          className={classes.dateText}
                          phaseType={PhaseTypeEnum.Bump5}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </Fragment>
            )}
          </Fragment>
        </Box>
      </Box>
    );
  };

  const getApprovedDate = (
    phaseType: PhaseTypeEnum,
    theJob: Job,
    getPhase: any,
  ) => {
    try {
      const jobPhase = getPhase(theJob?.jobPhases, phaseType);

      if (jobPhase.dateApproved) {
        const dateStart = dateWithoutServerTimezone(jobPhase.dateApproved);
        return `${
          dateStart.getMonth() + 1
        }/${dateStart.getDate()}/${dateStart.getFullYear()}`;
      }
      return '';
    } catch (error) {
      return '';
    }
  };

  const getResourceRequested = (
    phaseType: PhaseTypeEnum,
    theJob: Job,
    getPhase: any,
  ) => {
    try {
      const phaseJob = getPhase(theJob.jobPhases, phaseType);
      if (phaseJob) {
        if (phaseJob.dateRequested) {
          const dateRequested = dateWithoutServerTimezone(
            phaseJob.dateRequested,
          );

          return `${
            dateRequested.getMonth() + 1
          }/${dateRequested.getDate()}/${dateRequested.getFullYear()}`;
        }
      }
      return '';
    } catch (error) {
      return '';
    }
  };

  const getDateAssigned = (
    phaseType: PhaseTypeEnum,
    theJob: Job,
    getPhase: any,
  ) => {
    try {
      const phaseJob = getPhase(theJob.jobPhases, phaseType);
      if (phaseJob) {
        if (phaseJob.dateAssigned) {
          const dateAssigned = dateWithoutServerTimezone(phaseJob.dateAssigned);

          return `${
            dateAssigned.getMonth() + 1
          }/${dateAssigned.getDate()}/${dateAssigned.getFullYear()}`;
        }
      }
      return '';
    } catch (error) {
      return '';
    }
  };

  const TimeLine = () => {
    const classes = useStyles();

    const hangingSkipped = isSkippedPhase(
      theJob.jobPhases,
      PhaseTypeEnum.Hanging,
    );
    const finishingSkipped = isSkippedPhase(
      theJob.jobPhases,
      PhaseTypeEnum.Finishing,
    );
    const sprayingSkipped = isSkippedPhase(
      theJob.jobPhases,
      PhaseTypeEnum.Spraying,
    );

    const hangingPhase = getPhaseByType(
      theJob.jobPhases,
      PhaseTypeEnum.Hanging,
    );
    const finishingPhase = getPhaseByType(
      theJob.jobPhases,
      PhaseTypeEnum.Finishing,
    );
    const sprayingPhase = getPhaseByType(
      theJob.jobPhases,
      PhaseTypeEnum.Spraying,
    );
    const hangingStarting =
      !hangingSkipped &&
      hangingPhase &&
      isStartingApprovedPhase(hangingPhase, theJob);
    const finishingStarting =
      !finishingSkipped &&
      finishingPhase &&
      isStartingApprovedPhase(finishingPhase, theJob);
    const sprayingStarting =
      !sprayingSkipped &&
      sprayingPhase &&
      isStartingApprovedPhase(sprayingPhase, theJob);

    const skippedTextClass = (isSkipped: boolean) =>
      isSkipped ? classes.skippedPhaseText : undefined;

    return (
      <Box className={isMobile ? classes.whiteBoxMobile : classes.whiteBox}>
        <Box className={classes.timeline}>
          <Box display="flex" flexDirection="row" alignItems="center">
            {hangingSkipped || hangingStarting ? (
              <Box className={classes.circleGray}>
                <CheckIcon className={classes.iconCircle} />
              </Box>
            ) : JobPriority > 300 ? (
              <Box className={classes.signedStatusBadge}>
                <CheckIcon />
              </Box>
            ) : (
              <PanoramaFishEye fontSize="large" />
            )}
            <Box
              ml={1}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Typography
                className={skippedTextClass(
                  hangingSkipped || !!hangingStarting,
                )}
              >
                {hangingSkipped || hangingStarting
                  ? 'Hanging Approval Skipped'
                  : 'Hanging Approved'}
              </Typography>
              <Typography
                className={skippedTextClass(
                  hangingSkipped || !!hangingStarting,
                )}
              >
                {hangingStarting
                  ? ''
                  : getApprovedDate(PhaseTypeEnum['Hanging'], theJob, getPhase)}
              </Typography>
            </Box>
          </Box>
          <Divider style={{ width: '50px' }} variant="middle" />
          <Box display="flex" flexDirection="row" alignItems="center">
            {finishingSkipped || finishingStarting ? (
              <Box className={classes.circleGray}>
                <CheckIcon className={classes.iconCircle} />
              </Box>
            ) : (theJob.jobPhase.requested && JobPriority === 400) ||
              JobPriority > 400 ? (
              <Box className={classes.signedStatusBadge}>
                <CheckIcon />
              </Box>
            ) : (
              <PanoramaFishEye fontSize="large" />
            )}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              ml={1}
            >
              <Typography
                className={skippedTextClass(
                  finishingSkipped || !!finishingStarting,
                )}
              >
                {finishingSkipped || finishingStarting
                  ? 'Finisher Request Skipped'
                  : 'Finisher Requested'}
              </Typography>
              <Typography
                className={skippedTextClass(
                  finishingSkipped || !!finishingStarting,
                )}
              >
                {finishingStarting
                  ? ''
                  : getResourceRequested(
                      PhaseTypeEnum['Finishing'],
                      theJob,
                      getPhase,
                    )}
              </Typography>
            </Box>
          </Box>
          <Divider style={{ width: '50px' }} variant="middle" />
          <Box display="flex" flexDirection="row" alignItems="center">
            {finishingSkipped || finishingStarting ? (
              <Box className={classes.circleGray}>
                <CheckIcon className={classes.iconCircle} />
              </Box>
            ) : (theJob.jobPhase.requested && JobPriority === 400) ||
              JobPriority >= 500 ? (
              theJob.jobPhase.contractorId || JobPriority > 400 ? (
                <Box className={classes.signedStatusBadge}>
                  <CheckIcon />
                </Box>
              ) : (
                <PanoramaFishEye fontSize="large" />
              )
            ) : (
              <PanoramaFishEye fontSize="large" />
            )}
            <Box
              display="flex"
              ml={1}
              flexDirection="column"
              alignItems="center"
            >
              <Typography
                className={skippedTextClass(
                  finishingSkipped || !!finishingStarting,
                )}
              >
                {finishingSkipped || finishingStarting
                  ? 'Finisher Assignment Skipped'
                  : 'Finisher Assigned'}
              </Typography>
              <Typography
                className={skippedTextClass(
                  finishingSkipped || !!finishingStarting,
                )}
              >
                {finishingStarting
                  ? ''
                  : getDateAssigned(
                      PhaseTypeEnum['Finishing'],
                      theJob,
                      getPhase,
                    )}
              </Typography>
            </Box>
          </Box>
          <Divider style={{ width: '50px' }} variant="middle" />
          <Box
            display={
              finishingSkipped || finishingStarting || JobPriority > 400
                ? 'flex'
                : 'none'
            }
            flexDirection="row"
            alignItems="center"
          >
            <Box
              display={
                finishingSkipped || finishingStarting || JobPriority > 400
                  ? 'flex'
                  : 'none'
              }
              className={
                finishingSkipped || finishingStarting
                  ? classes.circleGray
                  : classes.signedStatusBadge
              }
            >
              <CheckIcon
                className={
                  finishingSkipped || finishingStarting
                    ? classes.iconCircle
                    : undefined
                }
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box
              display={
                finishingSkipped || finishingStarting || JobPriority > 400
                  ? 'none'
                  : 'flex'
              }
              flexDirection="row"
              alignItems="center"
            >
              <PanoramaFishEye fontSize="large" />
            </Box>
            <Box
              display="flex"
              ml={1}
              flexDirection="column"
              alignItems="center"
            >
              <Typography
                className={skippedTextClass(
                  finishingSkipped || !!finishingStarting,
                )}
              >
                {finishingSkipped || finishingStarting
                  ? 'Finisher Approval Skipped'
                  : 'Finisher Approved'}
              </Typography>
              <Typography
                className={skippedTextClass(
                  finishingSkipped || !!finishingStarting,
                )}
              >
                {finishingStarting
                  ? ''
                  : getApprovedDate(
                      PhaseTypeEnum['Finishing'],
                      theJob,
                      getPhase,
                    )}
              </Typography>
            </Box>
          </Box>
          <Divider style={{ width: '50px' }} variant="middle" />
          <Box display="flex" flexDirection="row" alignItems="center">
            {sprayingSkipped || sprayingStarting ? (
              <Box className={classes.circleGray}>
                <CheckIcon className={classes.iconCircle} />
              </Box>
            ) : (theJob.jobPhase.requested && JobPriority === 500) ||
              JobPriority > 500 ? (
              <Box className={classes.signedStatusBadge}>
                <CheckIcon />
              </Box>
            ) : (
              <PanoramaFishEye fontSize="large" />
            )}
            <Box
              display="flex"
              ml={1}
              flexDirection="column"
              alignItems="center"
            >
              <Typography
                className={skippedTextClass(
                  sprayingSkipped || !!sprayingStarting,
                )}
              >
                {sprayingSkipped || sprayingStarting
                  ? 'Sprayer Request Skipped'
                  : 'Sprayer Requested'}
              </Typography>
              <Typography
                className={skippedTextClass(
                  sprayingSkipped || !!sprayingStarting,
                )}
              >
                {sprayingStarting
                  ? ''
                  : getResourceRequested(
                      PhaseTypeEnum['Spraying'],
                      theJob,
                      getPhase,
                    )}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // Timeline for DrywallAndPaint jobs (shows all phases with horizontal scroll)
  const DrywallAndPaintTimeLine = () => {
    const classes = useStyles();
    const scrollRef = React.useRef<HTMLDivElement>(null);

    // Auto-scroll to right when phase is Trim2 or after (priority >= 900)
    React.useEffect(() => {
      if (scrollRef.current && theJob?.jobPhase?.priority >= 900) {
        // Scroll to the right
        scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
      }
    }, [theJob?.jobPhase?.priority]);

    const visiblePhases = getPhasesForJobType(
      PhaseStructureTypeEnum.DrywallAndPaint,
    )
      .filter(type => isPhaseIncluded(type, theJob?.job))
      .map(type => ({ type, label: getPhaseDisplayName(type) }));

    return (
      <div
        ref={scrollRef}
        className={isMobile ? classes.whiteBoxMobile : classes.whiteBox}
        style={{ overflowX: 'auto' }}
      >
        <Box className={classes.timeline} style={{ minWidth: 'max-content' }}>
          {visiblePhases.map((phase, index) => {
            const skipped = isSkippedPhase(theJob.jobPhases, phase.type);
            const phaseJob = getPhaseByType(theJob.jobPhases, phase.type);
            const isStarting =
              !skipped && phaseJob && isStartingApprovedPhase(phaseJob, theJob);
            return (
              <Fragment key={phase.type}>
                <Box className={classes.statusItem}>
                  {skipped || isStarting ? (
                    <Box className={classes.circleGray}>
                      <CheckIcon className={classes.iconCircle} />
                    </Box>
                  ) : isPhaseCompleted(theJob, phase.type) ? (
                    <Box className={classes.circleGreen}>
                      <CheckIcon className={classes.iconCircle} />
                    </Box>
                  ) : (
                    <Box
                      className={getCircleColor(
                        theJob,
                        phase.type,
                        classes,
                        getPhase,
                      )}
                    >
                      <Typography className={classes.statusNumber}>
                        {index + 1}
                      </Typography>
                    </Box>
                  )}
                  {!isMobile && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      {skipped || isStarting ? (
                        <Typography
                          className={`${classes.statusText} ${classes.skippedPhaseText}`}
                        >
                          {phase.label} Skipped
                        </Typography>
                      ) : (
                        <>
                          <Typography className={classes.statusText}>
                            {phase.label}
                          </Typography>
                          <TimelineJobPhase
                            job={theJob}
                            getPhase={getPhase}
                            className={classes.dateText}
                            phaseType={phase.type}
                          />
                        </>
                      )}
                    </Box>
                  )}
                </Box>
                {index < visiblePhases.length - 1 && (
                  <Box
                    className={
                      isMobile
                        ? classes.transparentSeparator
                        : classes.separator
                    }
                  />
                )}
              </Fragment>
            );
          })}
        </Box>
      </div>
    );
  };

  // Check if it's a DrywallAndPaint job
  const isDrywallAndPaint =
    theJob?.job?.phaseStructureType === PhaseStructureTypeEnum.DrywallAndPaint;

  return (
    <>
      {PhaseName === 'Materials' ||
      PhaseName === 'Garage' ? null : isDrywallAndPaint ? (
        <DrywallAndPaintTimeLine />
      ) : isInWarranty() ? (
        <BumpTimeLine />
      ) : (
        <TimeLine />
      )}
    </>
  );
};

export default StatusBar;
